import type { TreatmentSupportRequest } from '@/types';

export type CanStartReason = 'WRONG_STATUS' | 'NO_PROGRESS_REPORT';

/**
 * Gate for the "Start treatment" transition.
 *
 * A case may leave `approved_treatment` for `treatment_started` only after
 * at least one progress report has been uploaded (audit proof of start).
 */
export function canStartTreatment(
  req: Pick<TreatmentSupportRequest, 'status' | 'progressReports'>,
): { ok: true } | { ok: false; reason: CanStartReason } {
  if (req.status !== 'approved_treatment') return { ok: false, reason: 'WRONG_STATUS' };
  if ((req.progressReports ?? []).length === 0) return { ok: false, reason: 'NO_PROGRESS_REPORT' };
  return { ok: true };
}

/**
 * A progress report can only exist once treatment has actually begun, so the
 * FIRST report on an `approved_treatment` case should offer the coordinator
 * an immediate move to `treatment_started` (confirm dialog, not auto-flip —
 * guards against wrong-file uploads on real cases).
 */
export function shouldOfferStartTreatment(
  status: TreatmentSupportRequest['status'],
  previousReportCount: number,
  newReportCount: number,
): boolean {
  return status === 'approved_treatment' && previousReportCount === 0 && newReportCount > 0;
}
